import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { TreePine, LogIn, Menu, X, Sparkles } from "lucide-react";
import { useState, useEffect } from "react";

const navLinks = [
  { name: "Home", href: "/" },
  { name: "About", href: "/about" },
  { name: "Features", href: "/features" },
  { name: "Blog", href: "/blog" },
  { name: "Pricing", href: "/pricing" },
  { name: "Contact", href: "/contact" },
];

export const TopNav = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname === href;
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-premium ${
        scrolled 
          ? "py-3 bg-background/60 backdrop-blur-xl border-b border-border/30 shadow-subtle" 
          : "py-5 bg-transparent"
      }`}
    >
      <div className="container max-w-6xl mx-auto px-4 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className="relative">
            <TreePine className="w-7 h-7 text-accent transition-transform duration-300 group-hover:scale-110" />
            <Sparkles className="w-3 h-3 text-gold absolute -top-1 -right-1 animate-sparkle" />
          </div>
          <span className="font-serif font-bold text-xl text-foreground tracking-tight">Santa Pass</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              onClick={handleNavClick}
              className={`relative px-4 py-2 text-sm font-medium transition-colors duration-300 group ${
                isActive(link.href) ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {link.name}
              <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-gradient-gold rounded-full transition-all duration-300 ${
                isActive(link.href) ? 'w-3/4' : 'w-0 group-hover:w-3/4'
              }`} />
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/auth?mode=login" className="hidden md:block">
            <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground">
              <LogIn className="w-4 h-4" />
              Sign In
            </Button>
          </Link>
          <Link to="/auth?mode=signup" className="hidden md:block">
            <Button variant="christmas" size="sm">
              Get Started
            </Button>
          </Link>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2.5 rounded-xl bg-secondary/50 backdrop-blur-sm hover:bg-secondary transition-colors border border-border/30"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-5 h-5 text-foreground" />
            ) : (
              <Menu className="w-5 h-5 text-foreground" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-xl border-b border-border/30 shadow-elevated animate-fade-in-down">
          <nav className="container max-w-6xl mx-auto py-6 px-4 flex flex-col gap-2">
            {navLinks.map((link, index) => (
              <Link
                key={link.name}
                to={link.href}
                onClick={handleNavClick}
                className={`text-base font-medium transition-colors py-3 px-4 rounded-xl animate-fade-in ${
                  isActive(link.href) 
                    ? 'text-foreground bg-secondary/50' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {link.name}
              </Link>
            ))}
            <div className="flex flex-col gap-3 pt-4 mt-2 border-t border-border/30">
              <Link to="/auth?mode=login" onClick={handleNavClick}>
                <Button variant="glass" className="w-full gap-2">
                  <LogIn className="w-4 h-4" />
                  Sign In
                </Button>
              </Link>
              <Link to="/auth?mode=signup" onClick={handleNavClick}>
                <Button variant="christmas" className="w-full">
                  Get Started Free
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
